import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { User } from '../../types/user';
import { UserIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { formatDocumentDate } from '../../utils/formatters';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';

interface CustomerUsersManagerProps {
  organizationId: string;
  organizationName: string;
}

const CustomerUsersManager: React.FC<CustomerUsersManagerProps> = ({
  organizationId,
  organizationName,
}) => {
  const { t } = useLanguage();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  const handleToggleActive = async (user: any) => {
    try {
      console.log('Current user.isActive:', user.isActive);
      const newStatus = !user.isActive;
      console.log('New status will be:', newStatus);
      
      await updateDoc(doc(db, 'users', user.id), {
        isActive: newStatus
      });
      
      console.log('Updated successfully');
      toast.success(newStatus ? t('customerAccess.messages.activated') : t('customerAccess.messages.deactivated'));
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error(t('customerAccess.messages.toggleError'));
    }
  };

  // Real-time listener for users with this customerId
  useEffect(() => {
    if (!organizationId) {
      setUsers([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = query(
      collection(db, 'users'),
      where('customerId', '==', organizationId),
      where('role', '==', 'customer')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const userList = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            uid: doc.id,
            email: data.email || '',
            displayName: data.displayName || '',
            photoURL: data.photoURL || '',
            role: data.role || 'customer',
            organizationId: data.organizationId || '',
            customerId: data.customerId || '',
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
            lastLoginAt: data.lastLoginAt?.toDate(),
            isActive: data.isActive !== false, // Default true if not set
          } as any;
        });

        setUsers(userList);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching users:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [organizationId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Ügyfélportál felhasználók
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {users.length} regisztrált felhasználó
          </p>
        </div>
      </div>

      {/* User List */}
      {users.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            Még nincs regisztrált felhasználó
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Küldj meghívót a kapcsolattartóknak a regisztrációhoz.
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Felhasználó
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Utolsó bejelentkezés
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Státusz
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Műveletek
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                        <UserIcon className="h-5 w-5 text-white" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {user.displayName}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {user.lastLoginAt ? formatDocumentDate(user.lastLoginAt) : 'Még nem jelentkezett be'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {(user as any).isActive ? (
                        <>
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            Aktív
                          </span>
                        </>
                      ) : (
                        <>
                          <XCircleIcon className="h-5 w-5 text-red-500" />
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                            Letiltva
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleToggleActive(user)}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                        (user as any).isActive
                          ? 'text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50'
                          : 'text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50'
                      }`}
                    >
                      {(user as any).isActive ? 'Letiltás' : 'Aktiválás'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CustomerUsersManager;
